import { useState } from 'react';
import { MoreVertical, Pencil, IndianRupee, Power, PowerOff } from 'lucide-react';
import { motion } from 'motion/react';
import { ProductForm } from './ProductForm';
import { ProductPriceEditor } from './ProductPriceEditor';
import { ProductStatusControl } from './ProductStatusControl';
import type { AdminProductRow } from '../types';

export function ProductActionsDropdown({ product }: { product: AdminProductRow }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<null | 'edit' | 'price' | 'status'>(null);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center"
        aria-label="Product actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute right-0 mt-2 w-48 bg-surface-container-high border border-border-subtle rounded-lg shadow-xl z-30 overflow-hidden"
        >
          <Item icon={<Pencil className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('edit'); }}>
            Edit
          </Item>
          <Item icon={<IndianRupee className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('price'); }}>
            Update price
          </Item>
          {product.isActive ? (
            <Item icon={<PowerOff className="w-4 h-4" />} destructive onClick={() => { setOpen(false); setModal('status'); }}>
              Deactivate
            </Item>
          ) : (
            <Item icon={<Power className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('status'); }}>
              Activate
            </Item>
          )}
        </motion.div>
      )}

      <ProductForm open={modal === 'edit'} mode="edit" product={product} onClose={() => setModal(null)} />
      <ProductPriceEditor
        open={modal === 'price'}
        productId={product.id}
        productName={product.displayName}
        currentAmountInPaise={product.amountInPaise}
        currency={product.currency}
        onClose={() => setModal(null)}
      />
      <ProductStatusControl
        open={modal === 'status'}
        productId={product.id}
        productName={product.displayName}
        action={product.isActive ? 'deactivate' : 'activate'}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

function Item({
  children,
  icon,
  onClick,
  destructive,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-tight flex items-center gap-2 hover:bg-white/5 ' +
        (destructive ? 'text-red-400' : 'text-on-surface')
      }
    >
      {icon}
      {children}
    </button>
  );
}
